import style from './Note.module.scss';
import NoteSection from './NoteSection';
import React, { ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  noteHasPendingCareCuesResponses,
  selectNoteReview,
  selectNoteReviewStatus,
  selectProviderUserInfo,
  selectShowPoorReviewModal,
  useAppSelector,
  useAppDispatch,
  poorReviewModalClosed,
  poorReviewResponseSubmitted,
  selectIsEdEnabled,
  icd10CodeSelected,
  icd10DropdownOpened,
  isSingleSectionNote,
  selectIsNewNote,
  selectNoteMarkdownContent,
  selectCurrentVisitStatus,
  reevalSentenceUpdateRequested,
  selectReevalSentences,
} from '@ax/redux-store';
import CopyToClipboard from '@/components/CopyToClipboard';
import { NOTE_SECTION } from '@/constants/note-section';
import { useGetNoteParagraphsAndSentences } from '@/hooks/api/useGetNoteContent';
import {
  convertUTCtoLocalDate,
  convertUTCtoLocalTime,
  debounce,
  formatTimeToShortAMPM,
} from '@/utils';
import Banner from '@/components/Banner';
import aiGenerateNoteIcon from '@image/ai-generate-note.svg';
import monitorScheduleIcon from '@image/monitor-schedule.svg';
import sentMdsIcon from '@image/sent-to-mds-icon.svg';
import { NOTE_STATUS } from '@ax/constants';
import { ANALYTICS_CONSTANTS } from '@ax/constants';
import useAnalytics from '@/hooks/useAnalytics.tsx';
import { Loader, OverlayLoader } from '@ax/ui';
import NoteReview from './NoteReview';
import PoorReviewResponseModal from '@/modules/Note/PoorReviewResponseModal';
import NoteID from '@/modules/Note/NoteID';
import HSForm from '@/modules/Note/HSForm';
import Editor from '@/components/Editor/Editor.tsx';
import { useMutation } from '@apollo/client';
import { UPDATE_NOTE_PART } from '@ax/graphql';
import { UpdateNotePartVariables } from '@/constants/update-note-part-variables';
import ComplaintTitle from '@/components/ComplaintTitle/index.tsx';
import { Code } from '@ax/constants';
import { TemplateSelection } from '@ax/constants';
import { AddFreeTextVariables } from '@/modules/Note/NoteContent.tsx';
import { ADD_FREE_TEXT } from '@ax/graphql';
import StatusIcon from '@/modules/StatusIcon/StatusIcon.tsx';
import { statusHistorySelector } from '@ax/redux-store';
import { isAfter } from 'date-fns';
import ReevalSentenceItem from '@/components/ReevalSentenceItem/ReevalSentenceItem';
import { Sentence } from '@ax/constants/src/sentence';

interface NoteProps {
  readonly?: boolean;
}

const Note = ({ readonly }: NoteProps) => {
  const noteSectionRef = useRef<HTMLDivElement>(null);
  const hpiSectionRef = useRef<HTMLDivElement>(null);
  const intervalHistorySectionRef = useRef<HTMLDivElement>(null);
  const rosSectionRef = useRef<HTMLDivElement>(null);
  const peSectionRef = useRef<HTMLDivElement>(null);
  const apSectionRef = useRef<HTMLDivElement>(null);
  const mdmSectionRef = useRef<HTMLDivElement>(null);
  const progressSectionRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();
  const templates = useAppSelector((state) => state.note.templates);
  const providerUserInfo = useAppSelector(selectProviderUserInfo);
  const isSpecialtyOncology = providerUserInfo?.specialtyName?.toLowerCase().includes('oncology');
  const noteReviewStatus = useAppSelector(selectNoteReviewStatus);
  const noteReview = useAppSelector(selectNoteReview);
  const showPoorReviewModal = useAppSelector(selectShowPoorReviewModal);
  const showSingleSectionNote = useAppSelector(isSingleSectionNote);
  const isNewNote = useAppSelector(selectIsNewNote);
  const isNoteUploaded = useAppSelector(
    (state) =>
      state.selectedVisit.latestNoteStatus?.status === NOTE_STATUS.NOTE_UPLOADED_TO_MDS ||
      state.selectedVisit.latestNoteStatus?.status === NOTE_STATUS.NOTE_UPLOADED_TO_EHR,
  );
  const noteMarkdownContent = useAppSelector(selectNoteMarkdownContent);
  const [noteContent, setNoteContent] = useState('');
  const reevalSentences = useAppSelector(selectReevalSentences);

  const visitsState = useAppSelector((state) => state.selectedVisit);

  const noteId = visitsState.uuid;

  const [noteStatus, setNoteStatus] = useState(null);

  const visitSelectedStatus = useAppSelector(selectCurrentVisitStatus(noteId));

  const statusHistoryFiltered = useAppSelector(statusHistorySelector);

  const {
    hpiSectionSentences,
    intervalHistorySectionSentences,
    rosSectionSentences,
    peSectionSentences,
    apSectionSentences,
    mdmSectionSentences,
    progressSectionSentences,
    hpiSectionParagraphs,
    intervalHistoryParagraphs,
    rosSentences,
    peSentences,
    apSectionParagraphs,
    mdmSectionParagraphs,
    progressSentences,
    loading,
    isAIGeneratedNote,
    aINoteTime,
    generatedAt,
  } = useGetNoteParagraphsAndSentences({ noteId, noteStatus, style });

  const hasPendingCareCuesResponses = useAppSelector(noteHasPendingCareCuesResponses(noteId));
  const isEdEnabled = useAppSelector(selectIsEdEnabled);

  const dispatch = useAppDispatch();

  const [updateNotePart] = useMutation<{ updateNotePart: any }, UpdateNotePartVariables>(
    UPDATE_NOTE_PART,
  );
  const [addFreeText] = useMutation<{ addFreeText: any }, AddFreeTextVariables>(ADD_FREE_TEXT);

  useEffect(() => {
    setNoteStatus(visitSelectedStatus);
  }, [visitSelectedStatus]);

  const formatNoteSection = (
    ref: RefObject<HTMLDivElement>,
    sectionHeader: string,
    content: JSX.Element[],
    withHeader: boolean = true,
  ) => {
    return (
      <div
        style={{ fontSize: 0 }}
        ref={ref}
      >
        {withHeader && noteSectionRef && (
          <strong style={{ display: 'block' }}>{sectionHeader}</strong>
        )}
        {content}
      </div>
    );
  };

  const formatFullNoteWithHeader = (withHeader: boolean = true): JSX.Element => {
    const render = (
      ref: React.RefObject<HTMLDivElement>,
      section: string,
      content: JSX.Element[],
      withHeader: boolean,
    ): JSX.Element => formatNoteSection(ref, section, content, withHeader);

    return (
      <>
        {render(hpiSectionRef, NOTE_SECTION.hpi, hpiSectionParagraphs, withHeader)}
        {isSpecialtyOncology &&
          render(
            intervalHistorySectionRef,
            'Interval History',
            intervalHistoryParagraphs,
            withHeader,
          )}
        {render(rosSectionRef, NOTE_SECTION.ros, rosSentences, withHeader)}
        {render(peSectionRef, NOTE_SECTION.pe, peSentences, withHeader)}
        {!isEdEnabled && render(apSectionRef, NOTE_SECTION.ap, apSectionParagraphs, withHeader)}
        {isEdEnabled && render(mdmSectionRef, NOTE_SECTION.mdm, mdmSectionParagraphs, withHeader)}
        {isEdEnabled && render(progressSectionRef, 'Re-Evaluation', progressSentences, withHeader)}
      </>
    );
  };

  const noteSections = [
    {
      title: NOTE_SECTION.hpi,
      sectionName: NOTE_SECTION.hpi,
      content: hpiSectionSentences,
      ref: hpiSectionRef,
      displayName: NOTE_SECTION.hpi,
    },
    {
      title: 'Interval History',
      sectionName: NOTE_SECTION.intervalHistory,
      content: intervalHistorySectionSentences,
      isHidden: !isSpecialtyOncology,
      ref: intervalHistorySectionRef,
      displayName: 'Int.History',
    },
    {
      title: NOTE_SECTION.ros,
      sectionName: NOTE_SECTION.ros,
      content: rosSectionSentences,
      isHidden: false,
      ref: rosSectionRef,
      displayName: NOTE_SECTION.ros,
    },
    {
      title: NOTE_SECTION.pe,
      sectionName: NOTE_SECTION.pe,
      content: peSectionSentences,
      isHidden: false,
      ref: peSectionRef,
      displayName: NOTE_SECTION.pe,
    },
    {
      title: NOTE_SECTION.ap,
      sectionName: NOTE_SECTION.ap,
      content: apSectionSentences,
      isHidden: isEdEnabled,
      ref: apSectionRef,
      displayName: NOTE_SECTION.ap,
    },
    {
      title: NOTE_SECTION.mdm,
      sectionName: NOTE_SECTION.mdm,
      content: mdmSectionSentences,
      isHidden: !isEdEnabled,
      ref: mdmSectionRef,
      displayName: NOTE_SECTION.mdm,
    },
    {
      title: 'Re-Evaluation',
      sectionName: NOTE_SECTION.progress,
      content: progressSectionSentences,
      isHidden: !isEdEnabled,
      ref: progressSectionRef,
      displayName: 'Re-Eval',
    },
  ];

  const noteContentRef = (title: string, ref: React.RefObject<HTMLDivElement> | null) => {
    switch (title) {
      case NOTE_SECTION.ap:
        return isEdEnabled ? null : ref;
      case NOTE_SECTION.mdm:
      case 'Re-Evaluation':
        return isEdEnabled ? ref : null;
      case 'Interval History':
        return isSpecialtyOncology ? ref : null;
      default:
        return ref;
    }
  };

  const generateNoteBanners = useCallback((): ReactNode | null => {
    if (statusHistoryFiltered.length === 0) {
      if (isAIGeneratedNote) {
        return (
          <Banner
            icon={aiGenerateNoteIcon}
            text={`AI-note generated on ${convertUTCtoLocalDate(generatedAt)} at ${aINoteTime}`}
          />
        );
      }
    }

    return (
      <>
        {statusHistoryFiltered.map((status, index) => {
          const uploadDate = convertUTCtoLocalDate(status.createdAt);
          const uploadTime = formatTimeToShortAMPM(convertUTCtoLocalTime(status.createdAt));
          if (status.status === NOTE_STATUS.NOTE_UPLOADED_TO_EHR) {
            return (
              <Banner
                key={index}
                icon={monitorScheduleIcon}
                imgClassName={style.note__sent_to_ehr_icon}
                text={`Note uploaded to EHR ${uploadDate && `on ${uploadDate}`} at ${
                  uploadTime && `${uploadTime}`
                }`}
              />
            );
          }

          if (status.status === NOTE_STATUS.NOTE_UPLOADED_TO_MDS) {
            return (
              <Banner
                key={index}
                icon={sentMdsIcon}
                imgClassName={style.note__sent_to_mds_icon}
                text={`Note sent to MDS ${uploadDate && `on ${uploadDate}`} at ${
                  uploadTime && `${uploadTime}`
                }`}
              />
            );
          }
        })}
      </>
    );
  }, [statusHistoryFiltered]);

  const handleCopy = () => {
    const selection = window.getSelection();
    const copiedText = selection?.toString().replace(/\n/g, ' ');

    analytics.onNoteCopied({
      noteSection: ANALYTICS_CONSTANTS.copyMethods.selection,
      method: ANALYTICS_CONSTANTS.copyMethods.selection,
      wordsCount: copiedText?.split(' ').filter(Boolean).length,
      noteId,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const updateNoteContent = debounce(async (content) => {
    if (content === '') {
      return;
    }

    if (isNewNote) {
      await addFreeText({
        variables: {
          noteId: visitsState.uuid,
          SharedId: visitsState.uuid,
          Section: NOTE_SECTION.whole_note,
          ActionSource: 'User',
          Order: 1,
          Content: content as string,
        },
      });

      return;
    }

    await updateNotePart({
      variables: {
        noteId: visitsState.uuid,
        SharedId: visitsState.uuid,
        selection: noteMarkdownContent.selection,
        Content: content as string,
        ActionSource: 'User',
        UpdatedAt: new Date().toISOString(),
      },
    });
  }, 3000);

  const handleContentChange = (updatedContent: string): void => {
    setNoteContent(updatedContent);
  };

  const isEditorContentEditable = (): boolean => {
    return (
      (!isNoteUploaded) &&
      noteStatus !== NOTE_STATUS.CONTENT_PROCESSING
    );
  };


  const handleReevalEdit = (content: any, sentence: any) => {
    dispatch(
      reevalSentenceUpdateRequested({
        selection: sentence.selection,
        noteId: visitsState.uuid,
        content: content as string,
        sentence: sentence as Sentence,
      }),
    );
  };

  return (
    <div className={style.note__container}>
      <div className={style.note__heading}>
        <span>Note</span>
        <div className={style.note__heading__button_list}>
          {providerUserInfo && !showSingleSectionNote && (
            <>
              <CopyToClipboard
                contentRef={noteSectionRef}
                section="Note"
              />
              {noteSections.map((section, index) => {
                const contentRef = noteContentRef(section.title, section.ref);

                return contentRef !== null ? (
                  <CopyToClipboard
                    key={index}
                    contentRef={contentRef}
                    section={section.displayName}
                  />
                ) : null;
              })}
            </>
          )}
        </div>
      </div>

      <div ref={noteSectionRef}>{formatFullNoteWithHeader()}</div>
      <div>{formatFullNoteWithHeader(false)}</div>

      {loading ? (
        <Loader color="#344bc6" />
      ) : (
        <div
          onCopy={handleCopy}
          className={style.note__section__wrapper}
        >
          {hasPendingCareCuesResponses && <OverlayLoader />}
          {generateNoteBanners()}
          {noteStatus !== NOTE_STATUS.NEW_APPOINTMENT && (
            <div className={style.note_status_icon__container}>
              <StatusIcon status={noteStatus} />
            </div>
          )}
          <HSForm
            noteId={noteId}
            readonly={readonly}
          />
          {showSingleSectionNote && visitsState.uuid && (
            <div>
              <div
                ref={editorRef}
                aria-hidden="true"
              >
                <Editor
                  namespace={'Note'}
                  content={noteMarkdownContent.content}
                  isEditable={isEditorContentEditable()}
                  onEditorBlur={() => updateNoteContent(noteContent)}
                  onContentChange={handleContentChange}
                />
              </div>
              {
                <div className={style.template_codes__container}>
                  <div>
                    {isEdEnabled &&
                      showSingleSectionNote &&
                      reevalSentences?.map((sentence, index) => {
                        const lastStatus = statusHistoryFiltered[statusHistoryFiltered.length - 1];
                        if (
                          (isNoteUploaded &&
                            isAfter(
                              new Date(sentence.CreatedAt),
                              new Date(lastStatus?.createdAt),
                            )) ||
                          !lastStatus?.createdAt
                        ) {
                          return (
                            <ReevalSentenceItem
                              sentence={sentence}
                              key={index}
                              onBlur={(content, sentence) => handleReevalEdit(content, sentence)}
                            />
                          );
                        }
                        return null;
                      })}
                  </div>
                </div>
              }
              {!isEdEnabled && (
                <div className={style.template_codes__container}>
                  <div className={style.note__section__title}>
                    <strong>ICD-10 Codes</strong>
                  </div>
                  {templates.map((template: TemplateSelection, index: number) => {
                    return (
                      <ComplaintTitle
                        key={index}
                        complaint={template}
                        onValueChanged={(newCodeSelection, previousCode) => {
                          const complaint = template;
                          const newCode = complaint.codes.find(
                            (c: Code) => c.code === newCodeSelection?.code,
                          );
                          const currentSelection =
                            complaint.codes.find((c: Code) => c.code === previousCode?.code) ||
                            null;
                          if (currentSelection?.code === newCode?.code) {
                            return;
                          }
                          dispatch(
                            icd10CodeSelected({
                              currentSelection,
                              newCode,
                              noteId,
                              templateId: template.ComplaintId,
                              templateName: template.ComplaintName,
                              section: template.section,
                            }),
                          );
                        }}
                        onDropdownOpen={() => {
                          dispatch(
                            icd10DropdownOpened({
                              noteId,
                              templateName: template.ComplaintName,
                              section: template.section,
                            }),
                          );
                        }}
                        onlyShowCodes={true}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!showSingleSectionNote &&
            visitsState.uuid &&
            noteSections.map((section, index) => (
              <NoteSection
                key={index}
                title={section.title}
                section={section.sectionName}
                content={section.content}
                isHidden={section.isHidden}
                readonly={readonly}
              />
            ))}

          {!loading && noteId && (
            <div className={style.note_bottom_actions}>
              {!readonly && (
                <NoteReview
                  key={noteId}
                  noteId={noteId}
                  reviewStateStatus={noteReviewStatus}
                  review={noteReview}
                />
              )}
              {showPoorReviewModal && (
                <PoorReviewResponseModal
                  isOpen={showPoorReviewModal}
                  onHide={() => dispatch(poorReviewModalClosed({ noteId }))}
                  onSubmit={(review) => dispatch(poorReviewResponseSubmitted({ review }))}
                  review={noteReview}
                  reviewStateStatus={noteReviewStatus}
                />
              )}
              <NoteID noteId={noteId} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Note;
